import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import api from "./api";
import DialerPage from "./dialer";

export default function CallCenterWidgetPage() {
  const { tokenRef } = useParams();

  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  const [initialized, setInitialized] = useState(false);
  const [dialerSession, setDialerSession] = useState(null);
  const [error, setError] = useState("");

  async function initializeExtension(pendingToken = null) {
    let tk = pendingToken || token;
    if (!tk.trim()) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(
        `agents/call_centers/widgets/${tk}/dialer.json`,
      );
      // console.log(response);
      if (response.data.status === "success") {
        setDialerSession(response.data.data);
        setInitialized(true);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError("Unable to initialize extension.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function init() {
      if (tokenRef) {
        setToken(tokenRef);
        await initializeExtension(tokenRef);
      }
    }
    init();
  }, []);

  if (initialized) {
    return (
      <DialerPage
        dialerRef={token}
        token={dialerSession.token}
        session={dialerSession}
      />
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        // background: "linear-gradient(180deg, #f4f7fb 0%, #eef3f9 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 1,
        overflowX: "hidden",
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 360,
          borderRadius: 3,
          background: "#ffffff",
          border: "1px solid rgba(15,23,42,0.06)",
          boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2.5,
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="Logo"
              sx={{
                height: 48,
                objectFit: "contain",
              }}
            />
          </Box>

          <Typography
            variant="h5"
            sx={{
              color: "#111827",
              fontWeight: 700,
              textAlign: "center",
              mb: 1,
              letterSpacing: "-0.5px",
              fontSize: 24,
            }}
          >
            Laddar Voice
          </Typography>

          <Typography
            sx={{
              color: "#64748b",
              textAlign: "center",
              fontSize: 13,
              lineHeight: 1.6,
              mb: 3,
            }}
          >
            Enter your token to initialize your dialer session.
          </Typography>

          {error && (
            <Typography
              sx={{
                color: "#ef4444",
                fontSize: 13,
                textAlign: "center",
                mb: 2,
              }}
            >
              {error}
            </Typography>
          )}

          <TextField
            fullWidth
            autoFocus
            placeholder="Enter token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              mb: 2.5,

              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5,
                color: "#0f172a",
                background: "#f8fafc",

                "& fieldset": {
                  borderColor: "rgba(15,23,42,0.08)",
                },

                "&:hover fieldset": {
                  borderColor: "rgba(15,23,42,0.18)",
                },

                "&.Mui-focused fieldset": {
                  borderColor: "#2563eb",
                },
              },

              "& .MuiInputBase-input": {
                fontSize: 14,
              },

              "& .MuiInputBase-input::placeholder": {
                color: "#94a3b8",
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <span
                    className="material-icons"
                    style={{
                      color: "#94a3b8",
                      fontSize: 20,
                    }}
                  >
                    key
                  </span>
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            disableElevation
            variant="contained"
            onClick={() => initializeExtension()}
            disabled={loading || !token.trim()}
            endIcon={
              !loading && (
                <span className="material-icons" style={{ fontSize: 20 }}>
                  arrow_forward
                </span>
              )
            }
            sx={{
              height: 46,
              borderRadius: 2.5,
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              background: "linear-gradient(135deg, #2563eb 0%, #06b6d4 100%)",

              "&:hover": {
                background: "linear-gradient(135deg, #1d4ed8 0%, #0891b2 100%)",
              },

              "&.Mui-disabled": {
                background: "#e2e8f0",
                color: "#94a3b8",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Initialize Dialer"
            )}
          </Button>

          <Typography
            sx={{
              mt: 2.5,
              textAlign: "center",
              color: "#94a3b8",
              fontSize: 11,
            }}
          >
            Secure session initialization
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
